import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API, authedFetch, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, FileDown, Plus, Trash2, Pencil, Check, X, QrCode } from "lucide-react";
import { CATEGORY_LABELS, StatusBadge, ResultBadge, formatDate, daysUntil } from "../components/dpi";
import InspectionDialog from "../components/InspectionDialog";
import DeviceDialog from "../components/DeviceDialog";
import PhotoUploader from "../components/PhotoUploader";
import DocumentUploader from "../components/DocumentUploader";
import ManualsView from "../components/ManualsView";
import QrOverlay from "../components/QrOverlay";
import PdfDownloadOverlay from "../components/PdfDownloadOverlay";
import { useAuth } from "../context/AuthContext";function InlineEditDipendente({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVal(value || "");
  }, [value]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(val.trim());
      setEditing(false);
    } catch {
      /* handled by parent */
    }
    setSaving(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <div className="font-bold" data-testid="dipendente-value">{value || "—"}</div>
        <button
          onClick={() => setEditing(true)}
          className="bru-btn bru-btn-ghost !px-1.5 !py-0.5 !text-xs"
          data-testid="dipendente-edit-btn"
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        className="bru-input !py-1 !text-sm max-w-[180px]"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Nome Cognome"
        autoFocus
        data-testid="dipendente-edit-input"
      />
      <button onClick={save} disabled={saving} className="bru-btn bru-btn-primary !px-2 !py-1" data-testid="dipendente-save-btn">
        <Check className="w-4 h-4" />
      </button>
      <button onClick={() => { setEditing(false); setVal(value || ""); }} className="bru-btn bru-btn-secondary !px-2 !py-1" data-testid="dipendente-cancel-btn">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [device, setDevice] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIns, setEditingIns] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDelIns, setConfirmDelIns] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [editDeviceOpen, setEditDeviceOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, iRes] = await Promise.all([
        api.get(`/devices/${id}`),
        api.get(`/devices/${id}/inspections`),
      ]);
      setDevice(dRes.data);
      setInspections(iRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [id]);

  const deleteDevice = async () => {
    await api.delete(`/devices/${id}`);
    navigate("/");
  };

  const deleteInspection = async () => {
    if (!confirmDelIns) return;
    await api.delete(`/inspections/${confirmDelIns.id}`);
    setConfirmDelIns(null);
    load();
  };

  const saveDipendente = async (value) => {
    setSaveError("");
    try {
      const { data } = await api.patch(`/devices/${id}`, { dipendente: value });
      setDevice((prev) => ({ ...prev, ...data }));
    } catch (e) {
      setSaveError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      throw e;
    }
  };

  const savePhotos = async (photos) => {
    try {
      const { data } = await api.patch(`/devices/${id}`, { photos });
      setDevice((prev) => ({ ...prev, ...data }));
    } catch (e) {
      setSaveError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadPdf = async (inspectionId, code, date) => {
    setPdfDownloading(true);
    try {
      const res = await authedFetch(`${API}/inspections/${inspectionId}/pdf`);
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        alert(`Errore nella generazione del PDF (HTTP ${res.status}). ${txt.slice(0, 200)}`);
        return;
      }
      const blob = await res.blob();
      // Prefer the server-provided filename (ispezione_DPI_{brand}_{model}_{matricola}_{dipendente}.pdf)
      // so it matches the bulk ZIP naming convention.
      const cd = res.headers.get("content-disposition") || "";
      let filename = `ispezione_${code}_${date}.pdf`;
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(`Errore di rete durante il download PDF: ${e.message || e}`);
    } finally {
      setPdfDownloading(false);
    }
  };

  const downloadQr = () => setQrOpen(true);

  if (loading || !device) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="tag-label">CARICAMENTO…</div>
      </div>
    );
  }

  const exp = device.latest_inspection?.expiration_date || device.next_inspection_date;
  const du = daysUntil(exp);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="device-detail-page">
      <Link to="/" className="inline-flex items-center gap-2 mb-6 bru-link text-sm" data-testid="back-to-dashboard-link">
        <ArrowLeft className="w-4 h-4" />
        Torna alla Dashboard
      </Link>

      {device.life_expired && (
        <div className="mb-6 rounded-2xl border border-[#ff3b30]/30 bg-[#fff5f4] p-4 flex items-start gap-3" data-testid="life-expired-banner">
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#ff3b30] text-white flex items-center justify-center font-bold">!</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[#ff3b30]">Vita utile scaduta — DPI non idoneo</div>
            <div className="text-sm text-neutral-700 mt-0.5">
              La vita utile è terminata il <b>{formatDate(device.life_expiration_date)}</b>. Non è possibile registrare nuove ispezioni: il dispositivo deve essere sostituito.
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bru-card p-6 mb-8" data-testid="device-header">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="bru-badge bg-white">{CATEGORY_LABELS[device.category]}</span>
              <StatusBadge status={device.status} />
            </div>
            <div className="tag-label mb-1">CODICE · {device.code}</div>
            <h1 className="text-4xl font-bold mb-2 tracking-tight" >
              {device.client_name}
            </h1>
            {/* Primary info: identity + main data */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6">
              <div>
                <div className="tag-label">Dipendente</div>
                <InlineEditDipendente value={device.dipendente} onSave={saveDipendente} />
              </div>
              <div>
                <div className="tag-label">Marca</div>
                <div className="font-bold">{device.brand || "—"}</div>
              </div>
              <div>
                <div className="tag-label">Modello</div>
                <div className="font-bold">{device.model || "—"}</div>
              </div>
              <div>
                <div className="tag-label">Matricola</div>
                <div className="font-bold font-mono">
                  {device.serial_number || "—"}
                </div>
              </div>
            </div>

            {/* Hero: Prossima Scadenza highlighted */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-4">
              <div
                className={`rounded-2xl p-5 border ${
                  device.status === "scaduto" || device.status === "fallito"
                    ? "bg-[#fff5f4] border-[#ff3b30]/30"
                    : device.status === "in_scadenza"
                    ? "bg-[#fffaeb] border-[#ffcc00]/40"
                    : "bg-[#f5f5f7] border-neutral-200"
                }`}
                data-testid="next-expiry-hero"
              >
                <div className="tag-label">Prossima Scadenza</div>
                <div className="text-3xl md:text-4xl font-bold mt-1 leading-none">{formatDate(exp) || "—"}</div>
                {du !== null && (
                  <div
                    className={`text-sm font-semibold mt-2 ${
                      du < 0 ? "text-[#ff3b30]" : du <= 30 ? "text-[#a36b00]" : "text-neutral-600"
                    }`}
                  >
                    {du < 0 ? `Scaduto da ${Math.abs(du)} gg` : `${du} gg rimanenti`}
                  </div>
                )}
              </div>
              <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-4 grid grid-cols-2 gap-3">
                <div>
                  <div className="tag-label !text-[10px]">Vita Utile</div>
                  <div className="text-sm font-semibold">
                    {device.useful_life_years ? `${device.useful_life_years} anni` : "—"}
                  </div>
                </div>
                <div>
                  <div className="tag-label !text-[10px]">Ultima Ispezione</div>
                  <div className="text-sm font-semibold">{formatDate(device.last_inspection_date) || "—"}</div>
                </div>
                <div>
                  <div className="tag-label !text-[10px]">Data Produzione</div>
                  <div className="text-xs text-neutral-700">{formatDate(device.production_date) || "—"}</div>
                </div>
                <div>
                  <div className="tag-label !text-[10px]">Primo Utilizzo</div>
                  <div className="text-xs text-neutral-700">{formatDate(device.first_use_date) || "—"}</div>
                </div>
              </div>
            </div>
            {saveError && (
              <div className="mt-3 text-xs text-[#ff3b30]">{saveError}</div>
            )}
            {device.notes && (
              <div className="mt-5 pt-5 border-t-[1.5px] border-neutral-200">
                <div className="tag-label mb-1">Note</div>
                <div className="text-sm">{device.notes}</div>
              </div>
            )}
            <div className="mt-5 pt-5 border-t-[1.5px] border-neutral-200">
              <div className="tag-label mb-2">Foto DPI</div>
              {isAdmin ? (
                <PhotoUploader value={device.photos || []} onChange={savePhotos} max={3} />
              ) : (device.photos || []).length > 0 ? (
                <PhotoUploader value={device.photos || []} onChange={() => {}} readOnly />
              ) : (
                <div className="text-xs text-neutral-500">Nessuna foto caricata.</div>
              )}
            </div>
            {((device.documents || []).length > 0 || (device.manual_ids || []).length > 0) && (
              <div className="mt-5 pt-5 border-t-[1.5px] border-neutral-200">
                <div className="tag-label mb-2">Documenti</div>
                {(device.documents || []).length > 0 && (
                  <div className="mb-2">
                    <DocumentUploader value={device.documents || []} onChange={() => {}} readOnly />
                  </div>
                )}
                <ManualsView kind="dpi" manualIds={device.manual_ids || []} />
              </div>
            )}
          </div>
          {isAdmin && (
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
              <button
                onClick={() => { setEditingIns(null); setDialogOpen(true); }}
                className="bru-btn bru-btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                disabled={device.life_expired}
                title={device.life_expired ? "Vita utile scaduta: il DPI non può essere ispezionato" : ""}
                data-testid="add-inspection-button"
              >
                <Plus className="w-4 h-4" />
                Nuova Ispezione
              </button>
              <button
                onClick={() => setEditDeviceOpen(true)}
                className="bru-btn bru-btn-secondary"
                data-testid="edit-device-button"
              >
                <Pencil className="w-4 h-4" />
                Modifica Anagrafica
              </button>
              <button
                onClick={downloadQr}
                className="bru-btn bru-btn-secondary"
                data-testid="download-qr-device"
              >
                <QrCode className="w-4 h-4" />
                QR Code
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="bru-btn bru-btn-secondary !text-[#ff3b30]"
                data-testid="delete-device-button"
              >
                <Trash2 className="w-4 h-4" />
                Elimina DPI
              </button>
            </div>
          )}
          {!isAdmin && (
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
              <button onClick={downloadQr} className="bru-btn bru-btn-secondary" data-testid="download-qr-device">
                <QrCode className="w-4 h-4" />
                QR Code
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Inspections history */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="tag-label mb-1">STORICO · REVISIONI</div>
          <h2 className="text-3xl font-bold" >Ispezioni registrate</h2>
        </div>
        <div className="tag-label">{inspections.length} revision{inspections.length === 1 ? "e" : "i"}</div>
      </div>

      {inspections.length === 0 ? (
        <div className="bru-card p-10 text-center" data-testid="inspections-empty">
          <div className="tag-label mb-2">NESSUNA REVISIONE</div>
          <h3 className="text-xl font-bold mb-2">Questo DPI non è stato ancora ispezionato</h3>
          {isAdmin ? (
            <>
              <p className="text-neutral-600 mb-5 text-sm">
                Registra la prima ispezione per iniziare il tracciamento delle scadenze.
              </p>
              <button onClick={() => { setEditingIns(null); setDialogOpen(true); }} className="bru-btn bru-btn-primary" data-testid="empty-add-inspection-button">
                <Plus className="w-4 h-4" />
                Prima Ispezione
              </button>
            </>
          ) : (
            <p className="text-neutral-600 text-sm">
              Il DPI non ha ancora ricevuto una revisione da parte dell&apos;operatore.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="inspections-list">
          {inspections.map((ins, idx) => {
            const isLatest = idx === 0;
            return (
            <div
              key={ins.id}
              className={`bru-card ${isLatest ? "p-6 ring-1 ring-[#ff5a00]/20" : "p-4"}`}
              data-testid={`inspection-row-${ins.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-5 items-start">
                <div className="flex md:flex-col items-center md:items-start gap-4 md:min-w-[160px]">
                  {isLatest ? (
                    <div className="bg-neutral-900 text-white px-5 py-4 rounded-2xl">
                      <div className="text-[10px] uppercase tracking-[0.15em] opacity-60">Ultima Ispezione</div>
                      <div className="font-bold text-3xl leading-none mt-1.5">{formatDate(ins.inspection_date)}</div>
                    </div>
                  ) : (
                    <div className="bg-neutral-100 text-neutral-700 px-3 py-2 rounded-xl">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Ispezione</div>
                      <div className="font-semibold text-base leading-none mt-1">{formatDate(ins.inspection_date)}</div>
                    </div>
                  )}
                </div>
                <div className={`${isLatest ? "space-y-3" : "space-y-2"}`}>
                  <div className="flex gap-2 flex-wrap">
                    <ResultBadge result={ins.result} />
                    <span className="bru-badge bg-white">
                      VALIDITÀ {ins.expiration_months} {ins.expiration_months === 1 ? "MESE" : "MESI"}
                    </span>
                    <span className="bru-badge bg-[#f5f5f5]">SCADE {formatDate(ins.expiration_date)}</span>
                  </div>
                  {ins.signature && (
                    <div>
                      <div className="tag-label">Firma</div>
                      <div className={`font-bold italic`} style={{ fontFamily: "'Brush Script MT', cursive", fontSize: isLatest ? "18px" : "15px" }}>
                        {ins.signature}
                      </div>
                    </div>
                  )}
                  {ins.notes && (
                    <div>
                      <div className="tag-label mb-1">Note</div>
                      <div className="text-sm whitespace-pre-line">{ins.notes}</div>
                    </div>
                  )}
                  {(ins.photos || []).length > 0 && (
                    <div>
                      <div className="tag-label mb-2">Foto ispezione</div>
                      <PhotoUploader value={ins.photos} onChange={() => {}} readOnly />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => downloadPdf(ins.id, device.code, ins.inspection_date)}
                    className={`bru-btn bru-btn-secondary ${!isLatest ? "!px-3 !py-1.5 !text-xs" : ""}`}
                    data-testid={`download-pdf-${ins.id}`}
                  >
                    <FileDown className="w-4 h-4" />
                    Scarica Certificato
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => { setEditingIns(ins); setDialogOpen(true); }}
                        className={`bru-btn bru-btn-secondary ${!isLatest ? "!px-3 !py-1.5 !text-xs" : ""}`}
                        data-testid={`edit-inspection-${ins.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                        Modifica
                      </button>
                      <button
                        onClick={() => setConfirmDelIns(ins)}
                        className={`bru-btn bru-btn-secondary !text-[#ff3b30] ${!isLatest ? "!px-3 !py-1.5 !text-xs" : ""}`}
                        data-testid={`delete-inspection-${ins.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Elimina
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {isAdmin && (
        <InspectionDialog
          open={dialogOpen}
          deviceId={id}
          initial={editingIns}
          onClose={() => { setDialogOpen(false); setEditingIns(null); }}
          onSaved={() => {
            setDialogOpen(false);
            setEditingIns(null);
            load();
          }}
        />
      )}

      {isAdmin && (
        <DeviceDialog
          open={editDeviceOpen}
          initial={device}
          onClose={() => setEditDeviceOpen(false)}
          onSaved={() => {
            setEditDeviceOpen(false);
            load();
          }}
        />
      )}

      <QrOverlay
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        kind="dpi"
        id={id}
        title={device?.code || "DPI"}
        filename={`qr_${device?.code || "dpi"}`}
      />

      <PdfDownloadOverlay
        open={pdfDownloading}
        title="Generazione PDF in corso…"
        subtitle="Stiamo preparando il rapporto di ispezione."
      />

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-dialog">
          <div className="bru-card p-6 max-w-sm w-full">
            <div className="tag-label mb-1">CONFERMA</div>
            <h3 className="text-xl font-bold mb-3" >
              Eliminare questo DPI?
            </h3>
            <p className="text-sm text-neutral-600 mb-5">
              Verranno eliminate anche tutte le ispezioni ({device.inspections_count}) registrate. Operazione non reversibile.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(false)} className="bru-btn bru-btn-secondary" data-testid="cancel-delete-button">
                Annulla
              </button>
              <button onClick={deleteDevice} className="bru-btn bru-btn-danger" data-testid="confirm-delete-button">
                Elimina Tutto
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelIns && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-inspection-dialog">
          <div className="bru-card p-6 max-w-sm w-full">
            <div className="tag-label mb-1">CONFERMA</div>
            <h3 className="text-xl font-bold mb-3" >Eliminare questa ispezione?</h3>
            <p className="text-sm text-neutral-600 mb-5">
              L&apos;ispezione del {formatDate(confirmDelIns.inspection_date)} verrà rimossa. Operazione non reversibile.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelIns(null)} className="bru-btn bru-btn-secondary" data-testid="cancel-delete-inspection-button">Annulla</button>
              <button onClick={deleteInspection} className="bru-btn bru-btn-danger" data-testid="confirm-delete-inspection-button">Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
